import { useMemo } from 'react';
import {
  useSummary,
  useEvent,
  useCountdownTimer,
  useTicketsSelected,
  useAuth,
} from '@/store';
import useMsciBanner from '@/hooks/useMsciBanner';

const paymentConfigList = {
  mci: 'default_mci',
  msi: 'mci_msi',
  preSale: 'preventa_exclusiva',
  preSaleMsi: 'preventa_exclusiva_msi',
};

/**
 * Create event tracking decorators
 * @returns {Object} set of decorators and utilities
 */

export default function useTrackingDecorators() {
  const {
    summary: { discount, total_to_pay },
  } = useSummary();

  const {
    auth: { user },
  } = useAuth();

  const {
    event: {
      preferences: { currency },
    },
  } = useEvent();

  const { count } = useTicketsSelected();

  const { timeLeft } = useCountdownTimer();

  const { bannerKind: paymentConfig } = useMsciBanner();

  const decoratorFunctions = useMemo(() => {
    return {
      withCoupon: (trackFn) => {
        return (eventName, data) => {
          trackFn(eventName, {
            ...data,
            coupon_enabled: discount > 0,
          });
        };
      },
      withCurrency: (trackFn) => {
        return (eventName, data) => {
          trackFn(eventName, {
            ...data,
            currency,
          });
        };
      },
      withTotalPrice: (trackFn) => {
        return (eventName, data) => {
          trackFn(eventName, {
            ...data,
            total_price: total_to_pay,
          });
        };
      },
      withValue: (trackFn) => {
        return (eventName, data) => {
          trackFn(eventName, {
            ...data,
            value: total_to_pay,
          });
        };
      },
      withPaymentConfig: (trackFn) => {
        return (eventName, data) => {
          trackFn(eventName, {
            ...data,
            payment_config: paymentConfigList[paymentConfig],
          });
        };
      },
      withTotalTickets: (trackFn) => {
        return (eventName, data) => {
          trackFn(eventName, {
            ...data,
            total_tickets: count,
          });
        };
      },
      withTimeRemaining: (trackFn) => {
        return (eventName, data) => {
          trackFn(eventName, {
            ...data,
            time_seconds_remaining: timeLeft,
          });
        };
      },
      withUserPhone: (trackFn) => {
        return (eventName, data) => {
          trackFn(eventName, {
            ...data,
            user_phone: user?.phone,
          });
        };
      },
    };
  }, [count, currency, discount, paymentConfig, total_to_pay, user?.phone]);

  return decoratorFunctions;
}
